// Vector port of TournamentPrintableTable.js's renderResults().
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { formatPlayerName, formatDoublesPair } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawTableRow, hLine, drawText } from "./pdfKit";

export const CONTENT_WIDTH_PX = 900;
export const CONTENT_WIDTH_MM = px2mm(CONTENT_WIDTH_PX);

const HEADER_FS = px2pt(13);
const BODY_FS = px2pt(13);
const HEADER_H = lineHeightMm(HEADER_FS, 1.2) + px2mm(16);
const ROW_H = lineHeightMm(BODY_FS, 1.3) + px2mm(14);

const PLACE_W = px2mm(60);
const MATCHES_W = px2mm(70);
const WINS_W = px2mm(60);
const LOSSES_W = px2mm(60);
const DIFF_W = px2mm(60);
const RATING_W = px2mm(80);
const NAME_W = CONTENT_WIDTH_MM - PLACE_W - MATCHES_W - WINS_W - LOSSES_W - DIFF_W - RATING_W;

const buildRows = (list, grade) => list.map((item, index) => {
    let name = "—";
    if (grade === 1) name = formatPlayerName(item?.player || item);
    else if (grade === 2) {
        const players = item?.team?.players || item?.players || [];
        name = formatDoublesPair(players[0], players[1]);
    } else {
        name = item?.team?.name || item?.name || `Team #${index + 1}`;
    }
    return {
        place: (item?.tournament_place && item.tournament_place !== 0 && item.tournament_place !== "0") ? item.tournament_place : "—",
        name,
        matches: item?.tournament_matches ?? 0,
        wins: item?.tournament_wins ?? 0,
        losses: item?.tournament_losses ?? 0,
        diff: item?.games_diff != null ? `${item.games_diff > 0 ? "+" : ""}${item.games_diff}` : "—",
        points: item?.tournament_score ?? 0,
        alt: index % 2 === 1,
    };
});

export const measureResultsTable = (data, grade) => {
    const list = Array.isArray(data) ? data : (data?.results || []);
    if (!list.length) return px2mm(40);
    return HEADER_H + list.length * ROW_H;
};

export const drawResultsTable = (pdf, x, y, data, grade) => {
    const list = Array.isArray(data) ? data : (data?.results || []);
    let cy = y;

    if (!list.length) {
        pdf.setFillColor(T.superWhite);
        pdf.rect(x, cy, CONTENT_WIDTH_MM, px2mm(40), "F");
        drawText(pdf, t("No data available"), x + CONTENT_WIDTH_MM / 2, cy + px2mm(15), { fontSizePt: BODY_FS, color: T.muted, align: "center" });
        return cy + px2mm(40);
    }

    const headerCols = [
        { widthMm: PLACE_W, align: "center", text: t("Place") },
        { widthMm: NAME_W, align: "left", text: t("Participant"), padXMm: px2mm(10) },
        { widthMm: MATCHES_W, align: "center", text: t("Matches") },
        { widthMm: WINS_W, align: "center", text: t("Wins") },
        { widthMm: LOSSES_W, align: "center", text: t("Losses") },
        { widthMm: DIFF_W, align: "center", text: t("+/-") },
        { widthMm: RATING_W, align: "center", text: t("Rating") },
    ];
    hLine(pdf, x, cy, CONTENT_WIDTH_MM, T.border, px2mm(1));
    drawTableRow(pdf, { x, y: cy, totalWidthMm: CONTENT_WIDTH_MM, cols: headerCols, rowHeightMm: HEADER_H, bg: T.superWhite, borderColor: T.border, fontSizePt: HEADER_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
    cy += HEADER_H;

    buildRows(list, grade).forEach((r) => {
        const cols = [
            { widthMm: PLACE_W, align: "center", text: r.place, color: T.ink },
            { widthMm: NAME_W, align: "left", text: r.name, color: T.ink, padXMm: px2mm(10) },
            { widthMm: MATCHES_W, align: "center", text: r.matches, color: T.ink },
            { widthMm: WINS_W, align: "center", text: r.wins, color: T.ink },
            { widthMm: LOSSES_W, align: "center", text: r.losses, color: T.alarm },
            { widthMm: DIFF_W, align: "center", text: r.diff, color: T.ink },
            { widthMm: RATING_W, align: "center", text: r.points, bold: true, color: T.ink },
        ];
        drawTableRow(pdf, { x, y: cy, totalWidthMm: CONTENT_WIDTH_MM, cols, rowHeightMm: ROW_H, bg: r.alt ? T.superWhite : T.white, borderColor: T.border, fontSizePt: BODY_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
        cy += ROW_H;
    });

    return cy;
};
